import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import WorkCard from "@/components/base/WorkCard";
import ImageLightbox from "@/pages/detail/components/ImageLightbox";
import ShareModal from "@/pages/detail/components/ShareModal";
import { useFavorites } from "@/hooks/useFavorites";
import { formatCount, getCategoryById, getRelatedWorks, getWorkById } from "@/utils/gallery";

export default function DetailPage() {
  const { workId = "" } = useParams();
  const work = getWorkById(workId);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [activeImage, setActiveImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [toast, setToast] = useState("");

  const related = useMemo(() => (work ? getRelatedWorks(work, 6) : []), [work]);

  useEffect(() => {
    setActiveImage(0);
    if (work) {
      document.title = `${work.title} - 模特写真详情 - 国模白露`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute(
          "content",
          `${work.title}，模特${work.model}拍摄的写真作品，由摄影师${work.photographer}于${work.date}完成。${work.description.slice(0, 60)}`
        );
      }
    }
    window.scrollTo({ top: 0 });
  }, [work]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2000);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!work) {
    return (
      <div className="px-4 pb-24 pt-28 md:px-8 md:pt-32">
        <div className="mx-auto flex max-w-xl flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-node-tree text-3xl" />
          </div>
          <h1 className="mt-5 text-lg font-semibold text-foreground-950">作品不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-500">
            你查看的作品可能已被移除，返回图库继续浏览其他写真作品。
          </p>
          <Link
            to="/"
            className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-home-4-line" />
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = getCategoryById(work.categoryId);
  const favorited = isFavorite(work.id);

  const handleFavorite = () => {
    toggleFavorite(work.id);
    setToast(favorited ? "已取消收藏" : "已加入收藏");
  };

  const metaItems = [
    { label: "模特", value: work.model, icon: "ri-user-star-line" },
    { label: "摄影师", value: work.photographer, icon: "ri-camera-lens-line" },
    { label: "拍摄日期", value: work.date, icon: "ri-calendar-line" },
    { label: "拍摄风格", value: work.style, icon: "ri-palette-line" },
    { label: "拍摄场景", value: work.scene, icon: "ri-map-pin-2-line" },
    { label: "所属分类", value: category ? category.name : "—", icon: "ri-price-tag-3-line" },
  ];

  return (
    <div className="px-4 pb-20 pt-24 md:px-8 md:pb-28 md:pt-28">
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: "作品分类", to: category ? `/category/${category.id}` : undefined },
            ...(category ? [{ label: category.name, to: `/category/${category.id}` }] : []),
            { label: work.title },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
            <img
              src={work.images[activeImage]}
              alt={`${work.title} - 模特${work.model}写真作品`}
              title={`${work.title}写真作品`}
              className="h-auto max-h-[70vh] w-full cursor-zoom-in object-contain"
              onClick={() => setLightboxOpen(true)}
            />
            <button
              type="button"
              onClick={() => setLightboxOpen(true)}
              className="absolute bottom-4 right-4 flex items-center gap-2 whitespace-nowrap rounded-md bg-foreground-950/70 px-4 py-2 text-xs text-white backdrop-blur-sm transition-colors hover:bg-foreground-950/90"
            >
              <i className="ri-fullscreen-line" />
              点击放大预览
            </button>
          </div>

          <div className="mt-4 flex gap-3 overflow-x-auto pb-2 no-scrollbar">
            {work.images.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                onClick={() => setActiveImage(index)}
                className={`h-20 w-16 shrink-0 overflow-hidden rounded-md border-2 transition-colors md:h-24 md:w-20 ${
                  index === activeImage
                    ? "border-primary-500"
                    : "border-background-200 opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={image}
                  alt={`${work.title} 缩略图 ${index + 1}`}
                  className="h-full w-full object-cover object-top"
                />
              </button>
            ))}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            {category && (
              <Link
                to={`/category/${category.id}`}
                className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line" />
                {category.name}
              </Link>
            )}
            <h1 className="mt-3 text-2xl font-bold text-foreground-950">{work.title}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-primary-500" />
                {formatCount(work.views)} 浏览
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-heart-3-line text-accent-500" />
                {formatCount(work.likes)} 收藏
              </span>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-5">
              {metaItems.map((item) => (
                <div key={item.label}>
                  <p className="flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className={`${item.icon} text-primary-500`} />
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-medium text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-3 text-sm font-medium transition-colors ${
                  favorited
                    ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                    : "bg-primary-500 text-background-50 hover:bg-primary-600"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                {favorited ? "已收藏" : "收藏作品"}
              </button>
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-line" />
                分享
              </button>
            </div>

            <div className="mt-6 border-t border-background-200 pt-5">
              <p className="text-xs text-foreground-400">标签</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/list/fashion?tag=${encodeURIComponent(tag)}`}
                    className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900 transition-colors hover:bg-primary-100 hover:text-primary-800"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <section className="mt-12 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10">
        <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
          <span className="h-5 w-1 rounded-full bg-primary-500" />
          作品简介与拍摄背景
        </h2>
        <p className="mt-4 max-w-4xl text-sm leading-loose text-foreground-700">{work.description}</p>
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {metaItems.slice(0, 4).map((item) => (
            <div key={item.label} className="rounded-md bg-background-100 p-4">
              <p className="text-xs text-foreground-400">{item.label}</p>
              <p className="mt-1 text-sm font-medium text-foreground-900">{item.value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-bold text-foreground-950">
            <span className="h-5 w-1 rounded-full bg-primary-500" />
            相关推荐作品
          </h2>
          <Link
            to="/"
            className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
          >
            返回首页
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {related.map((item, index) => (
            <WorkCard key={item.id} work={item} index={index} />
          ))}
        </div>
      </section>

      {lightboxOpen && (
        <ImageLightbox
          images={work.images}
          index={activeImage}
          title={work.title}
          onClose={() => setLightboxOpen(false)}
          onChange={setActiveImage}
        />
      )}

      {shareOpen && <ShareModal title={work.title} onClose={() => setShareOpen(false)} />}

      {toast && (
        <div className="fixed bottom-8 left-1/2 z-[90] -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-2.5 text-sm text-white">
          {toast}
        </div>
      )}
    </div>
  );
}